import type { TextKey, Translated } from "@repo/shared/types";
import {
  type ComplianceCheckStatus,
  type DashboardPeriod,
  DEFAULT_DASHBOARD_PERIOD,
} from "@repo/shared/types";
import { CheckCircle2, CircleDashed, Flag, ListChecks, type LucideIcon } from "lucide-react";
import { useState } from "react";
import type { DashboardTarget } from "@/lib/api/dashboard";
import { useDashboardWidget } from "@/lib/hooks/use-dashboard";
import { useT } from "@/lib/i18n/use-t";
import { useDashboardFormat } from "../use-dashboard-format";
import { PeriodSwitch } from "./period-switch";
import { T } from "./review-status-widget.i18n";
import { SegmentBar } from "./segment-bar";
import { StageTiles } from "./stage-tiles";
import type { Tone } from "./tone";
import { WidgetCard } from "./widget-card";

type Label = TextKey<Translated<typeof T>>;

const STAGE: Record<ComplianceCheckStatus, { icon: LucideIcon; tone: Tone; label: Label }> = {
  not_reviewed: { icon: CircleDashed, tone: "muted", label: "notReviewed" },
  flagged: { icon: Flag, tone: "destructive", label: "flagged" },
  approved: { icon: CheckCircle2, tone: "success", label: "approved" },
};

export function ReviewStatusWidget({ target }: { target: DashboardTarget }) {
  const [period, setPeriod] = useState<DashboardPeriod>(DEFAULT_DASHBOARD_PERIOD);
  const { data, isLoading, error } = useDashboardWidget("review_status", target, { period });
  const t = useT(T);
  const { count } = useDashboardFormat();

  return (
    <WidgetCard
      icon={ListChecks}
      title={t.reviewStatus}
      description={t.reviewStatusDescription}
      actions={<PeriodSwitch value={period} onChange={setPeriod} />}
      isLoading={isLoading}
      error={error}
    >
      {data && (
        <>
          <SegmentBar
            segments={data.stages.map((s) => ({
              key: s.status,
              value: s.count,
              tone: STAGE[s.status].tone,
            }))}
          />
          <StageTiles
            tiles={data.stages.map((s) => ({
              key: s.status,
              icon: STAGE[s.status].icon,
              tone: STAGE[s.status].tone,
              count: count(s.count),
              label: t[STAGE[s.status].label],
              to: s.link,
            }))}
          />
        </>
      )}
    </WidgetCard>
  );
}
